import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import type { AuthOrganizationContext, AuthUser } from '@/features/auth'

interface AuthState {
  // Core authentication state
  user: AuthUser | null
  isAuthenticated: boolean
  isLoading: boolean
  isLoggingOut: boolean

  // Organization context
  currentOrganization: AuthOrganizationContext | null

  // Pure state management actions (no side effects)
  setUser: (user: AuthUser | null) => void
  setIsAuthenticated: (isAuthenticated: boolean) => void
  setIsLoading: (isLoading: boolean) => void
  setIsLoggingOut: (isLoggingOut: boolean) => void
  setCurrentOrganization: (org: AuthOrganizationContext | null) => void
  updateUserProfile: (updates: Partial<AuthUser>) => void
  reset: () => void

  // Pure computed getters (no side effects)
  hasRole: (role: string) => boolean
  hasOrganizationRole: (organizationId: string, role: string) => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      isLoading: true,
      isLoggingOut: false,
      currentOrganization: null,

      // Actions
      setUser: (user) => {
        // Validate user object before storing to prevent malformed data
        if (user && typeof user !== 'object') {
          return
        }
        set({ user })
      },
      setIsAuthenticated: (isAuthenticated) => set({ isAuthenticated }),
      setIsLoading: (isLoading) => set({ isLoading }),
      setIsLoggingOut: (isLoggingOut) => set({ isLoggingOut }),
      setCurrentOrganization: (org) => set({ currentOrganization: org }),

      updateUserProfile: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),

      reset: () =>
        set((state) => ({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isLoggingOut: state.isLoggingOut, // Preserve isLoggingOut during reset
          currentOrganization: null,
        })),

      // Computed getters
      hasRole: (role: string) => {
        const { currentOrganization } = get()
        // Check roles from currentOrganization (synced from API)
        // Case-insensitive: API returns Title Case ("Admin"), frontend uses lowercase ("admin")
        const needle = role.toLowerCase()
        return (
          currentOrganization?.roles?.some((r) => r.toLowerCase() === needle) ??
          false
        )
      },

      hasOrganizationRole: (_organizationId: string, role: string) => {
        const { currentOrganization } = get()
        // Roles are scoped to the current org in the store, so we only need
        // to check the role. The organizationId param is kept for API compat
        // but not compared — callers pass internal UUIDs while the store
        // holds the Kinde external ID, so the comparison always failed.
        const needle = role.toLowerCase()
        return (
          currentOrganization?.roles?.some((r) => r.toLowerCase() === needle) ??
          false
        )
      },
    }),
    {
      name: 'auth-store',
      // Persist auth store in localStorage to survive deploys and S3/CF/CDN reloads.
      // localStorage is considered safe here since we don't persist tokens in this store.
      storage: createJSONStorage(() => localStorage),
      // Only persist user data, not sensitive tokens
      partialize: (state) => ({
        user: state.user,
        currentOrganization: state.currentOrganization,
      }),
      version: 1,
      // Migrate malformed data from previous versions
      migrate: (persistedState: any, version: number) => {
        if (version === 0) {
          // Fix malformed user data (null strings, undefined strings, etc.)
          if (
            !persistedState?.user ||
            persistedState.user === 'null' ||
            persistedState.user === 'undefined' ||
            typeof persistedState.user === 'string'
          ) {
            persistedState.user = null
          }
          if (
            !persistedState?.currentOrganization ||
            persistedState.currentOrganization === 'null' ||
            persistedState.currentOrganization === 'undefined' ||
            typeof persistedState.currentOrganization === 'string'
          ) {
            persistedState.currentOrganization = null
          }
        }
        return persistedState
      },
    },
  ),
)
